import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  message,
  Table,
  Select,
  Space,
  Tag,
  DatePicker,
  Drawer,
  Popover,
  Tooltip
} from 'antd';
import {
  DownloadOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  UserOutlined
} from '@ant-design/icons';
import './index.css';
import apiClient, { getOpInfoList } from '../../utils/apiService';
import dayjs from 'dayjs';

function AnRecord() {
  const [loading, setLoading] = useState(false);
  const [opList, setOpList] = useState([]);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [anrecordData, setAnrecordData] = useState([]); // 新增状态用于存储从API获取的数据
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 抽屉相关状态
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);


  // 获取数据
  const fetchData = useCallback(async (params = {}) => {
    // 加个loading，在table
    setLoading(true);

    const { page = 1, size = 10, ...filters } = params;

    try {
      const response = await apiClient.post('/an-email-record/query', {
        page,
        size,
        ...filters,
      });

      const { records, total } = response?.data?.data;

      setLoading(false);
      setAnrecordData(records ? records : []);
      setTotal(total);
    } catch (error) {
      setAnrecordData([]);
      setTotal(0);
      messageApi.error(error.message || 'Data Fetch Failure');
      setLoading(false);
    }
  }, [messageApi]);

  // 重新发送邮件
  const resendEmail = useCallback(async (record) => {
    try {
      setLoading(true);
      const response = await apiClient.post('/shipping-order/send-an-email', {
        anLogId: record.id,
        hblNo: record.hblNo,
        modelId: record.modelId,
        fileUrl: record.fileUrl,
        consigneeEmail: record.consigneeEmail,
        notifyEmail: record.notifyEmail
      });

      if (response.data.code === 200) {
        messageApi.success('Email Resend Success');
        // 重新加载数据以更新状态
        form.resetFields();
        setPage(1);
        setPageSize(10);
        fetchData({ page: 1, size: 10 });
      } else {
        messageApi.error(response.data.message || 'Email Resend Failure');
      }
    } catch (error) {
      messageApi.error('Email Resend Failure: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  }, [messageApi, fetchData, form, page, pageSize]);


  // 初始化加载数据
  useEffect(() => {
    fetchData({ page: 0, size: 10 });

    // 获取op list数据
    const fetchOpListData = async () => {
      try {
        const response = await getOpInfoList();
        console.log(response)
        // 获取 op List
        setOpList(response.data.data);
      } catch (error) {
        console.error('Error fetching Op List data:', error);
      }
    };

    fetchOpListData();

  }, []);

  // 表单提交
  const onFinish = useCallback((values) => {

    const pageNum = 1;
    const pageSize = 10;

    const v = { ...(values || {}) };

    // 'all' 不参与查询
    const dropIfAll = (val) => {
      if (val == null) return undefined;
      const s = String(val).trim();
      return s.toLowerCase() === 'all' ? undefined : s;
    };

    // 日期格式
    const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm:ss') : undefined);

    // pic: 'Unassigned' -> null；'all' -> undefined
    let pic = v.pic;
    if (pic != null && String(pic).trim().toLowerCase() === 'unassigned') {
      pic = null;
    } else {
      pic = dropIfAll(pic);
    }


    // 组装参数
    const params = {
      page: pageNum,
      size: pageSize,

      hblNo: dropIfAll(v.hblNo),
      pic: pic,           // null 表示未分配

      startTime: fmtDate(v.startTime),
      endTime: fmtDate(v.endTime),
      sendStatus: dropIfAll(v.sendStatus),
    };

    // 去掉 undefined 字段
    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

    fetchData(params);
    setPage(pageNum);
    setPageSize(pageSize);
  }, [fetchData]);

  // 重置表单
  const onReset = useCallback(() => {
    form.resetFields();
    fetchData({ page: 0, size: 10 });
    setPage(1);
  }, [form, fetchData]);

  // 打开抽屉显示详情
  const showDrawer = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  // 关闭抽屉
  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedRecord(null);
  };

  // 表格列定义
  const columns = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: 'HBL',
      dataIndex: 'hblNo',
      key: 'hblNo',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'PIC',
      dataIndex: 'pic',
      key: 'pic',
      width: 120,
      render: (text) => (
        <Space>
          <UserOutlined />
          {text || '-'}
        </Space>
      ),
    },
    {
      title: 'Model ID',
      dataIndex: 'modelId',
      key: 'modelId',
      width: 120,
    },
    {
      title: 'Sender Email',
      dataIndex: 'senderEmail',
      key: 'senderEmail',
      width: 200,
    },
    {
      title: 'CC Emails',
      dataIndex: 'ccEmails',
      key: 'ccEmails',
      width: 200,
    },
    {
      title: 'Recipients',
      dataIndex: 'recipients',
      key: 'recipients',
      width: 200,
    },
    {
      title: 'Consignee Email',
      dataIndex: 'consigneeEmail',
      key: 'consigneeEmail',
      width: 200,
    },
    {
      title: 'Notify Email',
      dataIndex: 'notifyEmail',
      key: 'notifyEmail',
      width: 200,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: 250,
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 200,
    },
    {
      title: 'File URL',
      dataIndex: 'fileUrls',
      key: 'fileUrls',
      width: 300,
      render: (urls) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Array.isArray(urls) && urls.length > 0 ? (
            urls.map((url, index) => (
              <a 
                key={index} 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ wordBreak: 'break-all' }}
              >
                View File {urls.length > 1 ? `(${index + 1})` : ''}
              </a>
            ))
          ) : (
            <span style={{ color: '#999' }}>No files</span>
          )}
        </div>
      ),
    },
    {
      title: 'Send Status',
      dataIndex: 'sendStatus',
      key: 'sendStatus',
      width: 120,
      render: (status) => {
        // 0: 未发送, 1: 已发送
        const statusMap = {
          0: { color: 'red', text: 'Failure' },
          1: { color: 'green', text: 'Success' }
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Error Message',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: 'Send Time',
      dataIndex: 'sendTime',
      key: 'sendTime',
      width: 180,
    },
    {
      title: 'Created Time',
      dataIndex: 'createdTime',
      key: 'createdTime',
      width: 180,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<InfoCircleOutlined />}
            size="small"
            onClick={() => showDrawer(record)}
          />

          {record.sendStatus === 0 && (
            <Button
              type="text"
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => resendEmail(record)}
            />
          )}
        </Space>
      ),
      width: 150,
      fixed: 'right',
    },
  ], [navigate]);

  return (
    <div className="anrecord-container">
      {contextHolder}

      <div className="search-form">
        <Form
          form={form}
          name="search"
          onFinish={onFinish}
          onReset={onReset}
          layout="vertical"
        >
          <div className="form-items">
            <Form.Item
              name="hblNo"
              label="HBL"
              className="form-item"
            >
              <Input
                placeholder="Input HBL"
                allowClear
              />
            </Form.Item>

            <Form.Item
              name="pic"
              label="PIC"
              className="form-item"
            >
              <Select
                options={opList}
                placeholder="Input PIC"
                allowClear
                showSearch
              />
            </Form.Item>

            <Form.Item
              name="startTime"
              label="Start Time"
              className="form-item"
            >
              <DatePicker
                placeholder="Select startTime"
                style={{ width: '100%' }}
                showTime={{ format: 'HH:mm:ss' }}
                format={'MM/DD/YYYY HH:mm:ss'}
              />
            </Form.Item>

            <Form.Item
              name="endTime"
              label="endTime"
              className="form-item"
            >
              <DatePicker
                placeholder="Select endTime"
                style={{ width: '100%' }}
                showTime={{ format: 'HH:mm:ss' }}
                format={'MM/DD/YYYY HH:mm:ss'}
              />
            </Form.Item>

            <Form.Item
              name="sendStatus"
              label="sendStatus"
              className="form-item"
            >
              <Select
                options={[
                  { label: 'All', value: null },
                  { label: 'Success', value: 1 },
                  { label: 'Failure', value: 0 },
                ]}
                placeholder="Select Send Status"
                allowClear
              />
            </Form.Item>

          </div>

          <Form.Item>
            <Space
              style={{ marginTop: 10 }}
            >
              <Button type="primary" htmlType="submit">Search</Button>
              <Button htmlType="reset">Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
      <div className="anrecord-table">

        {/* <div
          className="anrecord-table-tools"
          style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 12px 0' }}
        >
        </div> */}

        <Table
          loading={loading}
          className='anrecord-table-table'
          dataSource={anrecordData.map((item) => ({ ...item, key: item.seq }))}
          rowKey='seq'
          columns={columns}
          size="small"
          scroll={{ x: 'max-content', y: 420 }}
          pagination={{
            total,
            pageSize,
            current: page,
            showQuickJumper: true,
            showSizeChanger: true,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);

              const formValues = form.getFieldsValue();
              const v = { ...(formValues || {}) };

              // Format dates the same way onFinish does
              const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm:ss') : undefined);

              const params = {
                page: newPage,
                size: newPageSize,
                hblNo: v.hblNo || undefined,
                pic: v.pic || undefined,
                startTime: fmtDate(v.startTime),
                endTime: fmtDate(v.endTime),
                sendStatus: v.sendStatus,
              };

              // Remove undefined fields
              Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

              fetchData(params);
            },
          }}
        />
      </div>

      {/* 数据详情 Drawer */}
      <Drawer
        title="Record Details"
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={600}
      >
        {selectedRecord && (
          <div className="record-detail">
            <div className="detail-section">
              <h3>Basic Information</h3>
              <div className="detail-item">
                <span className="detail-label">ID:</span>
                <span className="detail-value">{selectedRecord.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">HBL:</span>
                <span className="detail-value">{selectedRecord.hblNo}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Model ID:</span>
                <span className="detail-value">{selectedRecord.modelId}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">PIC:</span>
                <span className="detail-value">{selectedRecord.pic || '-'}</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Email Information</h3>
              <div className="detail-item">
                <span className="detail-label">Sender:</span>
                <span className="detail-value">{selectedRecord.senderEmail}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Recipients:</span>
                <span className="detail-value">{selectedRecord.recipients}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Consignee:</span>
                <span className="detail-value">{selectedRecord.consigneeEmail}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Notify:</span>
                <span className="detail-value">{selectedRecord.notifyEmail}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Subject:</span>
                <span className="detail-value">{selectedRecord.subject}</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>File Information</h3>
              {/* <div className="detail-item">
                <span className="detail-label">File Name:</span>
                <span className="detail-value">
                  {Array.isArray(selectedRecord.fileNames) && selectedRecord.fileNames.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedRecord.fileNames.map((name, index) => (
                        <Tag key={index} color="blue">
                          {name}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>No files</span>
                  )}
                </span>
              </div> */}
              <div className="detail-item">
                <span className="detail-label">File URL:</span>
                <span className="detail-value">
                  {Array.isArray(selectedRecord.fileUrls) && selectedRecord.fileUrls.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>

                      {selectedRecord.fileUrls.map((url, index) => {
                        return (
                        <Tooltip key={index} title={url}>
                          <Tag 
                            key={index} 
                            color="green"
                            icon={<DownloadOutlined />}
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.open(url, '_blank')}
                          >
                            ...{url.slice(-8)}
                          </Tag>
                        </Tooltip>
                        )
                      })}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>No files</span>
                  )}
                </span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Status Information</h3>
              <div className="detail-item">
                <span className="detail-label">Send Status:</span>
                <span className="detail-value">
                  <Tag color={selectedRecord.sendStatus === 1 ? 'green' : 'orange'}>
                    {selectedRecord.sendStatus === 1 ? 'Sent' : 'Unsent'}
                  </Tag>
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Error Message:</span>
                <span className="detail-value">{selectedRecord.errorMessage || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Send Time:</span>
                <span className="detail-value">{selectedRecord.sendTime}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created Time:</span>
                <span className="detail-value">{selectedRecord.createdTime}</span>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default AnRecord;